import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useApiQuery, useToast } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import { useCreditGate } from '../../payment/useCreditGate'
import { CreditOverdueBanner, GatedSaveButton } from '../../payment/CreditGateNotice'
import type { CoinPairList } from '../../api/types'

const EMPTY: CoinPairList = { id: null, exchangeId: '', tradingType: '', name: '', coinPairs: [] }

/** Equivalent of legacy `CoinpairlistEditComponent` (audit §6). Coin pairs
 * are stored as `"COIN1_COIN2"` strings — the exact legacy format
 * (`coinpairlist-edit.component.ts`: `this.newCoin1 + '_' + this.newCoin2`).
 * Credit-balance edit gating matches legacy exactly (`useCreditGate`) — the
 * legacy component also declared a `LogicOperand` class/fields, but never
 * used them in its template (dead code), so nothing to port there. */
export function CoinpairlistEditPage() {
  const { exchangeId = '', tradingType = '', id = 'new' } = useParams<{
    exchangeId: string
    tradingType: string
    id: string
  }>()
  const isNew = id === 'new'
  const navigate = useNavigate()
  const { showToast } = useToast()
  const gate = useCreditGate()
  const [list, setList] = useState<CoinPairList>({ ...EMPTY, exchangeId, tradingType })
  const [newCoin1, setNewCoin1] = useState('')
  const [newCoin2, setNewCoin2] = useState('USDT')
  const [saving, setSaving] = useState(false)

  const existing = useApiQuery(
    () => (isNew ? Promise.resolve(null) : screenerClient.viewCoinpairlist(exchangeId, tradingType, id)),
    [exchangeId, tradingType, id],
  )
  const momentums = useApiQuery(
    () => (isNew ? Promise.resolve([]) : screenerClient.searchCoinpairlistMomentums(exchangeId, tradingType, id)),
    [exchangeId, tradingType, id, isNew],
  )

  useEffect(() => {
    if (existing.data) setList({ ...existing.data, coinPairs: existing.data.coinPairs ?? [] })
  }, [existing.data])

  function addCoinPair() {
    if (!newCoin1 || !newCoin2 || newCoin1 === newCoin2) return
    const pair = `${newCoin1}_${newCoin2}`
    if (list.coinPairs.includes(pair)) return
    setList((prev) => ({ ...prev, coinPairs: [...prev.coinPairs, pair] }))
    setNewCoin1('')
  }

  function removeCoinPair(pair: string) {
    setList((prev) => ({ ...prev, coinPairs: prev.coinPairs.filter((p) => p !== pair) }))
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    try {
      if (isNew) {
        const created = await screenerClient.createCoinpairlist(exchangeId, tradingType, list)
        showToast('Coin-pair list created', 'success')
        navigate(`/${exchangeId}/${tradingType}/coinpairlists/${created.id}`)
      } else {
        const saved = await screenerClient.modifyCoinpairlist(exchangeId, tradingType, id, list)
        setList(saved)
        showToast('Coin-pair list saved', 'success')
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not save coin-pair list', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h1>{isNew ? 'New coin-pair list' : `Edit coin-pair list: ${list.name}`}</h1>

      <CreditOverdueBanner gate={gate} itemLabel="settings" />

      <form className="screener-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input value={list.name} onChange={(e) => setList((p) => ({ ...p, name: e.target.value }))} required />
        </label>
        <div>
          <strong>Coin pairs</strong>
          <ul>
            {list.coinPairs.map((pair) => (
              <li key={pair}>
                {pair.replace('_', ' / ')}{' '}
                <button type="button" onClick={() => removeCoinPair(pair)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div className="screener-actions">
            <input placeholder="COIN1 (e.g. BTC)" value={newCoin1} onChange={(e) => setNewCoin1(e.target.value.toUpperCase())} />
            <input placeholder="COIN2 (e.g. USDT)" value={newCoin2} onChange={(e) => setNewCoin2(e.target.value.toUpperCase())} />
            <button type="button" onClick={addCoinPair}>
              Add pair
            </button>
          </div>
        </div>
        <GatedSaveButton gate={gate} saving={saving} />
      </form>

      {!isNew && (
        <div className="screener-card">
          <h2>Momentums using this list</h2>
          {momentums.data && momentums.data.length === 0 && <p>None.</p>}
          <ul>
            {(momentums.data ?? []).map((m) => (
              <li key={m.id}>
                <Link to={`/${exchangeId}/${tradingType}/momentums/${m.id}`}>{m.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
